import { defineMessages, IntlShape } from 'react-intl';

import {
  displayInTimezone,
  TIME_24H_FORMAT,
  FORMAT_WITHOUT_SECONDS,
  getFormatDateByContinent,
  getTimezoneId,
  MONTH_DAY_YEAR_FORMAT,
} from 'src/utils/timeUtils';
import { updateText } from 'src/utils/stringUtils';
import { AlertLogItem } from './types';
import { Tenant } from 'src/types/tenants';
import { ALERT_METRIC_ENUM } from 'src/redux/data/constants';
import { ClosureReasons } from 'src/types/alerts';
import {
  getLongOutOfBedSuffix,
  getLongOutOfBedTriggerValue,
} from 'src/utils/alertHelpers';
import { personalEventLogMessages } from 'src/routes/IndividualDashboard/components/PersonalEventLogTab/constants';

export const messages = defineMessages({
  HR: {
    id: 'AlertLogCard.utils.HR',
    defaultMessage: 'HR',
  },
  RR: {
    id: 'AlertLogCard.utils.RR',
    defaultMessage: 'RR',
  },
  HR_BASELINE: {
    id: 'AlertLogCard.utils.HR_BASELINE',
    defaultMessage: 'HR',
  },
  RR_BASELINE: {
    id: 'AlertLogCard.utils.RR_BASELINE',
    defaultMessage: 'RR',
  },
  BED_EXIT: {
    id: 'AlertLogCard.utils.BED_EXIT',
    defaultMessage: 'BedExit',
  },
  unassignedDeviceDisconnectedInfo: {
    id: 'AlertLogCard.utils.unassignedDeviceDisconnectedInfo',
    defaultMessage: 'Unassigned disconnected device',
  },
  assignedDeviceDisconnectedInfo: {
    id: 'AlertLogCard.utils.assignedDeviceDisconnectedInfo',
    defaultMessage: 'Patient-assigned disconnected device',
  },
  allDevicesDisconnectedInfo: {
    id: 'AlertLogCard.utils.allDevicesDisconnectedInfo',
    defaultMessage: 'All devices are disconnected',
  },
  ABOVE: {
    id: 'AlertLogCard.utils.ABOVE',
    defaultMessage: 'ABOVE',
  },
  BELOW: {
    id: 'AlertLogCard.utils.BELOW',
    defaultMessage: 'BELOW',
  },
  BPM: {
    id: 'AlertLogCard.utils.BPM',
    defaultMessage: 'bpm',
  },
  BRPM: {
    id: 'AlertLogCard.utils.BRPM',
    defaultMessage: 'brpm',
  },
  room: {
    id: 'AlertLogCard.utils.room',
    defaultMessage: 'R',
  },
  roomLabel: {
    id: 'AlertLogCard.utils.roomLabel',
    defaultMessage: 'Room',
  },
  bed: {
    id: 'AlertLogCard.utils.bed',
    defaultMessage: 'Bed',
  },
  anAverageOf: {
    id: 'AlertLogCard.utils.anAverageOf',
    defaultMessage: 'An Average of',
  },
  is: {
    id: 'AlertLogCard.utils.is',
    defaultMessage: 'is',
  },
  for: {
    id: 'AlertLogCard.utils.for',
    defaultMessage: 'for',
  },
  of: {
    id: 'AlertLogCard.utils.of',
    defaultMessage: 'of',
  },
  from: {
    id: 'AlertLogCard.utils.from',
    defaultMessage: 'from',
  },
  hours: {
    id: 'AlertLogCard.utils.hours',
    defaultMessage:
      '{hoursCount } {hoursCount, select, 1 {hour} other {hours}}',
  },
  hour: {
    id: 'AlertLogCard.utils.hour',
    defaultMessage: 'hour',
  },
  min: {
    id: 'AlertLogCard.utils.min',
    defaultMessage: 'min',
  },
  high: {
    id: 'AlertLogCard.utils.high',
    defaultMessage: 'High',
  },
  low: {
    id: 'AlertLogCard.utils.low',
    defaultMessage: 'Low',
  },
  HR_RR_high: {
    id: 'AlertLogCard.utils.hr_rr_high',
    defaultMessage: 'High',
  },
  HR_RR_low: {
    id: 'AlertLogCard.utils.hr_rr_low',
    defaultMessage: 'Low',
  },
  days: {
    id: 'AlertLogCard.utils.days',
    defaultMessage: 'days',
  },
  baseline: {
    id: 'AlertLogCard.utils.baseline',
    defaultMessage: 'baseline',
  },
  since: {
    id: 'AlertLogCard.utils.since',
    defaultMessage: 'since',
  },
  on: {
    id: 'AlertLogCard.utils.on',
    defaultMessage: 'on',
  },
  or: {
    id: 'AlertLogCard.utils.or',
    defaultMessage: 'or',
  },
  and: {
    id: 'AlertLogCard.utils.and',
    defaultMessage: 'and',
  },
  out: {
    id: 'AlertLogCard.utils.out',
    defaultMessage: 'out',
  },
  more: {
    id: 'AlertLogCard.utils.more',
    defaultMessage: 'more',
  },
  alert: {
    id: 'AlertLogCard.utils.alert',
    defaultMessage: 'Notification {specialCharacter}',
  },
  onLastHours: {
    id: 'AlertLogCard.utils.onLastHours',
    defaultMessage: 'in the last 24 hours',
  },
  baselineDeviationText: {
    id: 'AlertLogCard.utils.baselineDeviationText',
    defaultMessage: 'baseline deviation',
  },
  vacantStation: {
    id: 'AlertLogCard.utils.vacantStation',
    defaultMessage: 'BedExit',
  },
  endedIn: {
    id: 'AlertLogCard.utils.endedIn',
    defaultMessage: 'Ended in',
  },
  stationIsReoccupied: {
    id: 'AlertLogCard.utils.stationIsReoccupied',
    defaultMessage: 'when station was reoccupied',
  },
  activity: {
    id: 'AlertLogCard.utils.activity',
    defaultMessage: 'Activity',
  },
  notification: {
    id: 'AlertLogCard.utils.notification',
    defaultMessage: 'Notification',
  },
  bedTimeHoursDiffer: {
    id: 'AlertLogCard.utils.bedTimeHoursDiffer',
    defaultMessage: 'Bed-Time hours differs from last',
  },
  frequentBedExitsBetween: {
    id: 'AlertLogCard.utils.frequentBedExitsBetween',
    defaultMessage: 'frequent Bed Exits between',
  },
  patient: {
    id: 'AlertLogCard.utils.patient',
    defaultMessage: 'Patient',
  },
  positionChangeDetails: {
    id: 'AlertLogCard.utils.positionChangeDetails',
    defaultMessage: 'Patient position has not been changed',
  },
  noConsentTooltip: {
    id: 'AlertLogCard.utils.noConsentTooltip',
    defaultMessage: 'No Consent - access to medical information is denied',
  },
  noConsentLabel: {
    id: 'AlertLogCard.utils.noConsentLabel',
    defaultMessage: 'No Consent',
  },
  noConsentAlertClosure: {
    id: 'AlertLogCard.utils.noConsentAlertClosure',
    defaultMessage: '{strongStart}Closed due to No Consent Status{strongEnd}',
  },
  lessThan: {
    id: 'AlertLogCard.utils.lessThan',
    defaultMessage: 'less than',
  },
});

const metricToUnit = Object.freeze({
  HR: 'BPM',
  RR: 'BRPM',
  HR_BASELINE: 'BPM',
  RR_BASELINE: 'BRPM',
});

export const getTooltipMessage = (
  alertItem: AlertLogItem,
  tenant: Tenant | undefined,
  intl: IntlShape,
): string => {
  const {
    startTime,
    endTime,
    thresholdMetric,
    deviceRoomName,
    deviceBedName,
    patientFirstName,
    patientLastName,
    reminderCount,
    deviceId,
    closureReason,
  } = alertItem;
  const alertClosedDueToConsent =
    closureReason === ClosureReasons.PATIENT_CONSENT_DISABLED;
  const noConsentAlertClosureDetails = alertClosedDueToConsent
    ? intl.formatMessage(messages.noConsentAlertClosure, {
        strongStart: '<strong>',
        strongEnd: '</strong>',
      })
    : '';
  const timezone = getTimezoneId(tenant?.timeZoneId);

  const timestampText = displayInTimezone(
    startTime,
    timezone,
    getFormatDateByContinent(FORMAT_WITHOUT_SECONDS),
  );
  const locationText = updateText(
    ' |',
    `${updateText(
      ` ${intl.formatMessage(messages.room)} `,
      deviceRoomName,
    )}${updateText(` ${intl.formatMessage(messages.bed)} `, deviceBedName)}`,
  );
  const patientText = updateText(
    ' |',
    `${updateText(' ', patientFirstName)}${updateText(' ', patientLastName)}`,
  );

  if (thresholdMetric === ALERT_METRIC_ENUM.ALL_DEVICES_DISCONNECTED) {
    const timestamp = displayInTimezone(
      startTime,
      timezone,
      getFormatDateByContinent(FORMAT_WITHOUT_SECONDS),
    );
    const endTimestamp = endTime
      ? displayInTimezone(
          endTime,
          timezone,
          getFormatDateByContinent(FORMAT_WITHOUT_SECONDS),
        )
      : 'N/A';
    const { city, countryCode } = tenant?.address || {};
    const locationText = `${timestamp} ${city || ''}, ${
      countryCode || ''
    }, ${timezone}.`;
    const infoText = `All devices on tenant ${
      tenant?.tenantName || ''
    } were disconnected until ${endTimestamp}. Please verify WiFi/power stability.`;

    return `${locationText} ${infoText}`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.DEVICE_DISCONNECTED) {
    const timestamp = displayInTimezone(
      startTime,
      timezone,
      getFormatDateByContinent(FORMAT_WITHOUT_SECONDS),
    );

    return `${intl.formatMessage(personalEventLogMessages.deviceName)} ${deviceId} ${intl.formatMessage(messages.on)} ${intl.formatMessage(messages.roomLabel)} ${deviceRoomName || ''} ${intl.formatMessage(messages.bed)} ${
      deviceBedName || ''
    } Last connection to cloud on ${timestamp}.`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.BED_EXIT) {
    const endTimeStamp = endTime
      ? displayInTimezone(endTime, timezone, TIME_24H_FORMAT)
      : 'N/A';

    return `${timestampText}${locationText}${patientText} | ${intl.formatMessage(
      messages.vacantStation,
    )}. ${intl.formatMessage(
      messages.endedIn,
    )} ${endTimeStamp} ${alertClosedDueToConsent ? noConsentAlertClosureDetails : intl.formatMessage(messages.stationIsReoccupied)}`;
  }

  if (
    thresholdMetric === ALERT_METRIC_ENUM.HR_BASELINE ||
    thresholdMetric === ALERT_METRIC_ENUM.RR_BASELINE
  ) {
    const {
      thresholdPreposition,
      deviationPercentage,
      deviationHours,
      baselineValue,
      baselineDaysInterval,
      actualBaselineIntervalHours,
    } = alertItem;
    return `${timestampText} ${locationText} ${patientText} 
    | ${intl.formatMessage(
      thresholdPreposition === 'ABOVE' ? messages.high : messages.low,
    )} ${intl.formatMessage(messages[thresholdMetric])} - ${intl.formatMessage(
      messages.baselineDeviationText,
    )} ${intl.formatMessage(messages.of)} ${Math.floor(
      Math.abs(deviationPercentage),
    )}% ${intl.formatMessage(messages.for)} ${intl.formatMessage(
      messages.hours,
      {
        hoursCount: deviationHours,
      },
    )} ${intl.formatMessage(messages.from)} 
     ${actualBaselineIntervalHours ? intl.formatMessage(messages.lessThan) : ''}
    ${baselineDaysInterval} ${intl.formatMessage(messages.days, {
      specialCharacter: '-',
    })} ${intl.formatMessage(messages.baseline)}
    ${Math.round(baselineValue)}. ${intl.formatMessage(
      messages[thresholdMetric],
    )} ${intl.formatMessage(
      messages.baselineDeviationText,
    )} ${intl.formatMessage(messages.alert, {
      specialCharacter: '#',
    })}${reminderCount} ${intl.formatMessage(messages.onLastHours)}. ${noConsentAlertClosureDetails}`;
  }

  if (
    thresholdMetric === ALERT_METRIC_ENUM.HR ||
    thresholdMetric === ALERT_METRIC_ENUM.RR
  ) {
    const {
      averageInterval,
      thresholdPreposition,
      thresholdValue,
      averageValue,
    } = alertItem;

    const averageDetailsText = updateText(
      ' |',
      ` ${intl.formatMessage(messages.anAverageOf)} ${Math.round(
        averageInterval / 60,
      )} ${intl.formatMessage(messages.min)} ${intl.formatMessage(
        messages.is,
      )} ${intl.formatMessage(
        messages[thresholdPreposition],
      )} ${thresholdValue} ${intl.formatMessage(
        messages[metricToUnit[thresholdMetric]],
      )} (${Math.round(averageValue)} ${intl.formatMessage(
        messages[metricToUnit[thresholdMetric]],
      )})`,
    );

    const alertReminderNumberText = updateText(
      ' | ',
      `${intl.formatMessage(
        thresholdPreposition === 'ABOVE'
          ? messages.HR_RR_high
          : messages.HR_RR_low,
      )} ${intl.formatMessage(messages[thresholdMetric])} ${intl.formatMessage(
        messages.alert,
        { specialCharacter: '#' },
      )}${reminderCount} ${intl.formatMessage(messages.onLastHours)}.`,
    );

    return `${timestampText}${locationText}${patientText} ${averageDetailsText} ${alertReminderNumberText} ${noConsentAlertClosureDetails}`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.LONG_OUT_OF_BED) {
    const { triggerValue } = alertItem;
    const longOutOfBedTriggerValue = getLongOutOfBedTriggerValue(triggerValue);
    const longOutOfBedTriggerValueSuffix = getLongOutOfBedSuffix(triggerValue);
    const longOutOfBedTooltipEnd =
      triggerValue >= 60
        ? `${intl.formatMessage(messages.or)} ${intl.formatMessage(messages.more)}`
        : '';

    const averageDetailsText = `| ${intl.formatMessage(messages.patient)} ${intl.formatMessage(messages.is)} ${intl.formatMessage(messages.out)} ${intl.formatMessage(messages.of)} ${intl.formatMessage(messages.bed)} ${intl.formatMessage(messages.for)} ${longOutOfBedTriggerValue} ${intl.formatMessage(longOutOfBedTriggerValueSuffix)} ${longOutOfBedTooltipEnd}`;

    return `${timestampText}${locationText}${patientText} ${averageDetailsText}. ${noConsentAlertClosureDetails}`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.POSITION_CHANGE) {
    const averageDetailsText = `| ${intl.formatMessage(messages.positionChangeDetails)}`;

    return `${timestampText}${locationText}${patientText} ${averageDetailsText}. ${noConsentAlertClosureDetails}`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.BED_TIME_BASELINE) {
    const { deviationPeriodDays = 0, triggerValue } = alertItem;

    const averageDetailsText = `| ${intl.formatMessage(messages.activity)} ${intl.formatMessage(messages.notification)} | ${triggerValue ?? '-'}% ${intl.formatMessage(messages.bedTimeHoursDiffer)} ${deviationPeriodDays} ${intl.formatMessage(messages.days, { specialCharacter: '-' })} ${intl.formatMessage(messages.baseline)}`;

    return `${timestampText}${locationText}${patientText} ${averageDetailsText}. ${noConsentAlertClosureDetails}`;
  }

  if (thresholdMetric === ALERT_METRIC_ENUM.BED_EXIT_FREQUENCY) {
    const { triggerValue } = alertItem;
    const startDateStamp = displayInTimezone(
      startTime,
      timezone,
      getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT),
    );
    const startTimeStamp = displayInTimezone(
      startTime,
      timezone,
      TIME_24H_FORMAT,
    );
    const endDateStamp = endTime
      ? displayInTimezone(
          endTime,
          timezone,
          getFormatDateByContinent(MONTH_DAY_YEAR_FORMAT),
        )
      : '';
    const endTimeStamp = endTime
      ? displayInTimezone(endTime, timezone, TIME_24H_FORMAT)
      : '';

    const averageDetailsText = `| ${intl.formatMessage(messages.activity)} ${intl.formatMessage(messages.alert, { specialCharacter: '' })} | ${triggerValue} ${intl.formatMessage(messages.frequentBedExitsBetween)} ${startTimeStamp} ${intl.formatMessage(messages.on)} ${startDateStamp}  ${intl.formatMessage(messages.and)} ${endTimeStamp} ${intl.formatMessage(messages.on)} ${endDateStamp}`;

    return `${timestampText}${locationText}${patientText} ${averageDetailsText}. ${noConsentAlertClosureDetails}`;
  }

  return '';
};
